import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import { usePermission } from '../../../../hooks/usePermission';
import AccessReviewService from '../../../../services/access-review.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';
import { SortOrder, Modules, AuthRoutes, NonAuthRoutes } from '../../../../enums';


interface EntryRow {
  id: number;
  user_id: number;
  user_snapshot: {
    full_name: string | null;
    email: string;
    role_name: string;
    product_scope: number[];
    last_login: string | null;
  };
  action: 'pending' | 'certified' | 'revoked';
  last_certified_at: string | null;
}

interface ReviewData {
  id: number;
  name: string;
  status: 'in_progress' | 'complete';
  total_users: number;
}

const AccessReviewDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManage = usePermission('access-review:manage');

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'full_name' });
  const [review, setReview] = useState<ReviewData | null>(null);
  const [entries, setEntries] = useState<EntryRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [refreshToken, setRefreshToken] = useState(0);
  const [revokeTarget, setRevokeTarget] = useState<'selected' | 'all' | EntryRow | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { loading } = useFetch({
    service: AccessReviewService,
    func: 'get',
    payload: id,
    dependency: [id, refreshToken],
    onSuccess: (data: any) => {
      setReview(data?.review ?? null);
      setEntries(data?.entries ?? []);
      setSelectedIds(new Set());
    },
    onError: () => {
      setReview(null);
      setEntries([]);
    },
  });

  const pendingEntries = useMemo(() => entries.filter((e) => e.action === 'pending'), [entries]);
  const actionedCount = entries.length - pendingEntries.length;
  const isComplete = review?.status === 'complete';

  const toggleSelect = useCallback((entryId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }, []);

  const toggleSelectAllPending = useCallback(() => {
    setSelectedIds((prev) => (prev.size === pendingEntries.length ? new Set() : new Set(pendingEntries.map((e) => e.id))));
  }, [pendingEntries]);

  const handleCertify = useCallback(
    async (entryIds?: number[]) => {
      if (!id) return;
      setActionLoading(true);
      try {
        await AccessReviewService.certify(id, entryIds);
        toast.success(<Toast message={t('iam.accessReviews.detail.pageTexts.certifySuccess')} />, { icon: false });
        setRefreshToken((v) => v + 1);
      } catch (err) {
        // interceptor already surfaces the error toast
      } finally {
        setActionLoading(false);
      }
    },
    [id, t]
  );

  const closeRevokeModal = useCallback(() => setRevokeTarget(null), []);

  const handleRevokeConfirm = useCallback(async () => {
    if (!id || !revokeTarget) return;
    const entryIds =
      revokeTarget === 'all' ? undefined : revokeTarget === 'selected' ? Array.from(selectedIds) : [revokeTarget.id];

    setActionLoading(true);
    try {
      await AccessReviewService.revoke(id, entryIds);
      toast.success(<Toast message={t('iam.accessReviews.detail.pageTexts.revokeSuccess')} />, { icon: false });
      setRefreshToken((v) => v + 1);
      closeRevokeModal();
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [id, revokeTarget, selectedIds, closeRevokeModal, t]);

  const handleMarkComplete = useCallback(async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await AccessReviewService.complete(id);
      toast.success(<Toast message={t('iam.accessReviews.detail.pageTexts.completeSuccess')} />, { icon: false });
      navigate(`${Modules.iam}${AuthRoutes.accessReviews}`);
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [id, navigate, t]);

  const headCells: HeadCellType[] = useMemo(() => {
    const cells: HeadCellType[] = [];
    if (!isComplete) {
      cells.push({ id: 'select', sortEnabled: false, width: '4%', label: '' });
    }
    cells.push(
      { id: 'full_name', sortEnabled: true, width: '17%', label: t('iam.accessReviews.detail.pageTexts.table.columns.name') },
      { id: 'email', sortEnabled: true, width: '18%', label: t('iam.accessReviews.detail.pageTexts.table.columns.email') },
      { id: 'role_name', sortEnabled: true, width: '13%', label: t('iam.accessReviews.detail.pageTexts.table.columns.role') },
      { id: 'product_scope', sortEnabled: true, width: '13%', label: t('iam.accessReviews.detail.pageTexts.table.columns.scope') },
      { id: 'last_login', sortEnabled: true, width: '12%', label: t('iam.accessReviews.detail.pageTexts.table.columns.lastLogin') },
      { id: 'last_certified_at', sortEnabled: true, width: '12%', label: t('iam.accessReviews.detail.pageTexts.table.columns.lastCertified') },
      { id: 'actions', sortEnabled: false, width: isComplete ? '17%' : '13%', label: t('iam.accessReviews.detail.pageTexts.table.columns.actions'), textAlign: 'right' },
    );
    return cells;
  }, [t, isComplete]);

  const Row = useCallback(
    ({ item, className }: { item: EntryRow; className?: string }) => (
      <tr className={className}>
        {!isComplete && (
          <td>
            {item.action === 'pending' && (
              <input
                type="checkbox"
                className="form-check-input"
                checked={selectedIds.has(item.id)}
                onChange={() => toggleSelect(item.id)}
              />
            )}
          </td>
        )}
        <td>{item.user_snapshot.full_name ?? '—'}</td>
        <td>{item.user_snapshot.email}</td>
        <td>{item.user_snapshot.role_name}</td>
        <td>{item.user_snapshot.product_scope.length ? item.user_snapshot.product_scope.join(', ') : t('iam.accessReviews.detail.pageTexts.globalScope')}</td>
        <td>{item.user_snapshot.last_login ? new Date(item.user_snapshot.last_login).toLocaleString() : '—'}</td>
        <td>{item.last_certified_at ? new Date(item.last_certified_at).toLocaleDateString() : t('iam.accessReviews.detail.pageTexts.never')}</td>
        <td className="text-end">
          {item.action === 'pending' ? (
            !isComplete && (
              <RowActions
                actions={[
                  {
                    id: 'certify',
                    label: t('iam.accessReviews.detail.pageTexts.certify') || 'Certify',
                    icon: <i className="ri-check-line" />,
                    onClick: () => handleCertify([item.id]),
                  },
                  {
                    id: 'revoke',
                    label: t('iam.accessReviews.detail.pageTexts.revoke') || 'Revoke',
                    icon: <i className="ri-close-line" />,
                    danger: true,
                    onClick: () => setRevokeTarget(item),
                  },
                ]}
              />
            )
          ) : (
            <StatusBadge
              label={
                item.action === 'certified'
                  ? t('iam.accessReviews.detail.pageTexts.certified')
                  : t('iam.accessReviews.detail.pageTexts.revoked')
              }
              colour={item.action === 'certified' ? '#2e7d32' : '#757575'}
            />
          )}
        </td>
      </tr>
    ),
    [isComplete, selectedIds, toggleSelect, handleCertify, t]
  );

  const headerSection = useMemo(
    () =>
      !isComplete && (
        <div className="col-12 d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="select-all-pending"
              checked={pendingEntries.length > 0 && selectedIds.size === pendingEntries.length}
              onChange={toggleSelectAllPending}
              disabled={pendingEntries.length === 0}
            />
            <label className="form-check-label" htmlFor="select-all-pending">
              {t('iam.accessReviews.detail.pageTexts.selectedCount', { count: selectedIds.size })}
            </label>
          </div>
          <div className="d-flex gap-2">
            <Button
              className="btn btn-outline-success btn-sm"
              text={t('iam.accessReviews.detail.pageTexts.certifyAll')}
              onClick={() => handleCertify()}
              disabled={pendingEntries.length === 0 || actionLoading}
            />
            <Button
              className="btn btn-outline-success btn-sm"
              text={t('iam.accessReviews.detail.pageTexts.certifySelected')}
              onClick={() => handleCertify(Array.from(selectedIds))}
              disabled={selectedIds.size === 0 || actionLoading}
            />
            <Button
              className="btn btn-outline-danger btn-sm"
              text={t('iam.accessReviews.detail.pageTexts.revokeSelected')}
              onClick={() => setRevokeTarget('selected')}
              disabled={selectedIds.size === 0 || actionLoading}
            />
          </div>
        </div>
      ),
    [isComplete, pendingEntries, selectedIds, toggleSelectAllPending, handleCertify, actionLoading, t]
  );

  const sortedEntries = useMemo(() => sortData(entries, orderBy, order), [entries, orderBy, order]);

  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedEntries.slice(start, start + perPage);
  }, [sortedEntries, page, perPage]);

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total: entries.length,
      loading,
      data: paginatedEntries,
      pagination: true,
      headerSection,
      noDataFoundMessage: t('iam.accessReviews.detail.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, entries.length, loading, paginatedEntries, headerSection, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  if (!canManage) {
    return <Navigate replace to={{ pathname: Modules.error + NonAuthRoutes.forbidden }} />;
  }

  if (loading && !review) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  const revokeCount =
    revokeTarget === 'all' ? pendingEntries.length : revokeTarget === 'selected' ? selectedIds.size : revokeTarget ? 1 : 0;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">{review?.name}</h4>
          <p className="text-muted mb-0 small">
            {t('iam.accessReviews.detail.pageTexts.progress', { actioned: actionedCount, total: entries.length })}
          </p>
        </div>
        {!isComplete && (
          <Button
            className="btn btn-primary"
            text={t('iam.accessReviews.detail.pageTexts.markComplete')}
            onClick={handleMarkComplete}
            loading={actionLoading}
          />
        )}
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!revokeTarget}
        onClose={closeRevokeModal}
        title={t('iam.accessReviews.detail.pageTexts.revokeModal.title')}
        content={t('iam.accessReviews.detail.pageTexts.revokeModal.message', { count: revokeCount })}
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeRevokeModal} />
            <Button
              className="btn btn-danger"
              text={t('iam.accessReviews.detail.pageTexts.revokeModal.confirm')}
              onClick={handleRevokeConfirm}
              loading={actionLoading}
            />
          </>
        }
      />
    </>
  );
};

export default AccessReviewDetail;
